import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createGuestTicket } from "../store/ticketsSlice";

export default function GuestTicketForm({ eventId, eventTitle, onSuccess, onClose }) {
    const dispatch = useDispatch();
    const { loading, error } = useSelector((state) => state.tickets);

    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phoneNumber: "",
    });

    const [validationErrors, setValidationErrors] = useState({
        fullName: "",
        email: "",
        phoneNumber: "",
    });

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [ticketDetails, setTicketDetails] = useState(null);

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Clear validation error when user starts typing
        if (validationErrors[name]) {
            setValidationErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }
    };

    // Validate form
    const validateForm = () => {
        const errors = {};
        let isValid = true;

        if (!formData.fullName.trim()) {
            errors.fullName = "Full name is required";
            isValid = false;
        }

        if (!formData.email.trim()) {
            errors.email = "Email is required";
            isValid = false;
        } else if (!emailRegex.test(formData.email)) {
            errors.email = "Please enter a valid email address";
            isValid = false;
        }

        if (!formData.phoneNumber.trim()) {
            errors.phoneNumber = "Phone number is required";
            isValid = false;
        }

        setValidationErrors(errors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const guestTicketData = {
            eventId,
            fullName: formData.fullName,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
        };

        dispatch(createGuestTicket(guestTicketData))
            .unwrap()
            .then((response) => {
                console.log("Guest ticket created successfully:", response);
                setTicketDetails(response.ticket);
                setShowSuccessModal(true);

                // Reset form
                setFormData({
                    fullName: "",
                    email: "",
                    phoneNumber: "",
                });

                if (onSuccess) {
                    onSuccess(response);
                }
            })
            .catch((error) => {
                console.error("Error creating guest ticket:", error);
            });
    };

    // Handle success modal close
    const handleSuccessClose = () => {
        setShowSuccessModal(false);
        if (onClose) {
            onClose();
        }
    };

    return (
        <>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <h3 className="text-xl font-semibold mb-4">Get Your Ticket</h3>
                <p className="text-sm text-gray-600 mb-4">
                    Fill in your details below to receive your ticket via email
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Full Name Input */}
                    <div>
                        <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                            Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${validationErrors.fullName ? "border-red-500" : "border-gray-300"
                                }`}
                            placeholder="John Doe"
                        />
                        {validationErrors.fullName && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.fullName}</p>
                        )}
                    </div>

                    {/* Email Input */}
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${validationErrors.email ? "border-red-500" : "border-gray-300"
                                }`}
                            placeholder="john@example.com"
                        />
                        {validationErrors.email && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
                        )}
                    </div>

                    {/* Phone Number Input */}
                    <div>
                        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="tel"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleInputChange}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${validationErrors.phoneNumber ? "border-red-500" : "border-gray-300"
                                }`}
                            placeholder="+1234567890"
                        />
                        {validationErrors.phoneNumber && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.phoneNumber}</p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full ${loading ? "bg-gray-400" : "bg-red-500 hover:bg-red-600"
                            } text-white py-3 rounded-md transition duration-300 font-medium`}
                    >
                        {loading ? "Processing..." : "Get Your Ticket"}
                    </button>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                            {error}
                        </div>
                    )}
                </form>

                <p className="text-xs text-gray-500 mt-4 text-center">
                    Your ticket will be sent to your email address
                </p>
            </div>

            {/* Loading Modal */}
            {loading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-lg shadow-lg text-center animate-fadeIn">
                        <div className="flex flex-col items-center space-y-4">
                            <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                            <p className="text-lg font-semibold text-gray-700">
                                Processing your ticket...
                            </p>
                            <p className="text-sm text-gray-500">
                                This might take a few seconds, please wait.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && ticketDetails && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-lg shadow-lg text-center animate-fadeIn max-w-md">
                        <div className="flex flex-col items-center space-y-4">
                            <svg
                                className="w-16 h-16 text-green-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                            <h3 className="text-2xl font-bold text-gray-800">
                                Ticket Created Successfully!
                            </h3>
                            <div className="text-left w-full bg-gray-50 p-4 rounded-md space-y-2">
                                <p className="text-sm">
                                    <span className="font-semibold">Event:</span> {ticketDetails.eventName}
                                </p>
                                <p className="text-sm">
                                    <span className="font-semibold">Name:</span> {ticketDetails.fullName}
                                </p>
                                <p className="text-sm">
                                    <span className="font-semibold">Email:</span> {ticketDetails.userEmail}
                                </p>
                                <p className="text-sm">
                                    <span className="font-semibold">Phone:</span> {ticketDetails.userPhoneNumber}
                                </p>
                            </div>
                            <p className="text-sm text-gray-600">
                                Your ticket has been sent to <strong>{ticketDetails.userEmail}</strong>
                            </p>
                            <p className="text-xs text-gray-500">
                                Please check your email (including spam folder) for the ticket PDF
                            </p>
                            <button
                                onClick={handleSuccessClose}
                                className="mt-4 px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
